import StackdriverErrorReporter from "stackdriver-errors-js";
import { BUILD_COMMIT } from "../../shared/constants/buildInfo";
import { BUILD_ENV } from "../../shared/constants/environment";
import { tokenStore } from "./tokenStore";

const SERVICE_NAME = "wingspan-app";

export type ErrorContext = Record<string, unknown>;

/**
 * The reporter mutates `context` in place — that is how `setUser` works — but
 * the published type definitions leave the property out.
 */
type Reporter = StackdriverErrorReporter & { context: ErrorContext };

/**
 * App-wide error sink, backed by GCP Error Reporting via
 * `stackdriver-errors-js`. The library owns the parts that are easy to get
 * wrong: resolving stacks against the deployed source maps, reassembling them
 * into the V8 shape the Error Reporting parser expects, and the global
 * `onerror` / `onunhandledrejection` handlers.
 */
class ErrorReportingService {
  private reporter = new StackdriverErrorReporter() as Reporter;
  private isStarted = false;

  /**
   * Installs the library's global handlers, so an uncaught exception or an
   * unhandled rejection is reported without any call site of ours.
   */
  start(): void {
    const key = process.env.REACT_APP_STACKDRIVER_ERRORS;

    // `start` throws without an API key and a project id, and a local build
    // has neither. Reports fall back to the console until it has run.
    if (this.isStarted || !key || !BUILD_ENV) {
      return;
    }

    this.reporter.start({
      key,
      projectId: `wingspan-${BUILD_ENV}`,
      service: SERVICE_NAME,
      // The old reporter sent `process.env.version`, which the build never
      // defines, so nothing was attributable to a release.
      version: BUILD_COMMIT || BUILD_ENV
    });

    this.isStarted = true;
  }

  report(error: unknown, context?: ErrorContext): void {
    if (!this.isStarted) {
      console.error(error, ...(context ? [context] : []));
      return;
    }

    // Only v3 sessions populate the token store, so this stays unset for a
    // session that has not been migrated yet.
    const accountId = tokenStore.getIdentity()?.accountId;

    // `report` takes no per-report context, so the extra fields are swapped
    // into the shared object it reads. Replacing the property rather than
    // mutating it is what makes this safe: `report` snapshots the object into
    // its payload synchronously, so the in-flight report keeps these fields
    // and no later report can see them.
    const baseContext = this.reporter.context;
    this.reporter.context = {
      ...baseContext,
      ...context,
      ...(accountId ? { user: accountId } : {})
    };

    const sent = this.reporter.report(
      // The library only accepts an Error or a message string.
      error instanceof Error || typeof error === "string"
        ? error
        : String(error),
      // A message string gets its stack synthesized inside `report`, whose own
      // frame the library drops. The second frame to drop is this method, so
      // the caller ends up on top.
      { skipLocalFrames: 2 }
    );

    this.reporter.context = baseContext;

    // Reporting runs on error paths, so it must never leave a rejected promise
    // behind — a failed report would otherwise be reported itself, in a loop.
    void sent.catch(() => {});
  }
}

export const errorReportingService = new ErrorReportingService();
